import * as store from "./customStore";
import * as storeActions from "../models/store/storeActions";
import * as baseService from "../models/services/baseService";
import { ConfirmationDialogType } from "../models/store/storeTypes";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { IApiResponse } from "ui/Scripts/source/models/common/apiResponse";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IInstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";

//AppUtils
export class AppUtils {
    static showSuccessMessage(message: string) {
        store.customStore.dispatch(storeActions.showSuccessMessage(message));
    }

    static showErrorMessage(message: string) {
        store.customStore.dispatch(storeActions.showErrorMessage(message));
    }

    static showApiErrorMessage(e) {
        AppUtils.showErrorMessage(baseService.getErrorMessageFromError(e));
    }

    static isString(data: any): data is string {
        return typeof data === "string";
    }

    static isIApiResponse(data: any): data is IApiResponse {
        if (AppUtils.isString(data)) return false;
        return data && "message" in data;
    }

    static async callService<T>(serviceCall: () => Promise<T>, showSuccessMessage: boolean = true, disableLoadIndicator: boolean = false, onFinally: (result?: T) => void = null): Promise<T> {
        if (!disableLoadIndicator) {
            store.customStore.dispatch(storeActions.fetchStart());
        }
        let result: T = null;
        try {
            result = await serviceCall();
            if (showSuccessMessage) {
                if (AppUtils.isIApiResponse(result) && result.message) {
                    AppUtils.showSuccessMessage(result.message);
                } else if (AppUtils.isString(result) && result) {
                    AppUtils.showSuccessMessage(result);
                }
            }
            return result;
        } catch (e) {
            result = null;
            AppUtils.showApiErrorMessage(e);
            return Base.getPromiseResult<T>(null);
        } finally {
            if (!disableLoadIndicator) {
                store.customStore.dispatch(storeActions.fetchEnd());
            }
            if (onFinally) {
                onFinally(result);
            }
        }
    }

    static showConfirmationDialog(message: string, showCancelButton: boolean = false, yesConfirmationText: string = null): Promise<ConfirmationDialogResult> {
        return new Promise<ConfirmationDialogResult>((resolve) => {
            store.customStore.dispatch(storeActions.setConfirmation(ConfirmationDialogType.Warning, Translations.Warning, message,
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                    resolve(ConfirmationDialogResult.Yes);
                },
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                    resolve(ConfirmationDialogResult.No);
                },
                showCancelButton
                    ? () => {
                        store.customStore.dispatch(storeActions.clearConfirmation());
                        resolve(ConfirmationDialogResult.Cancel);
                    }
                    : null
                , yesConfirmationText
            ));
        });
    }

    static async validate(checkErrors: () => Promise<boolean>, getWarnings: () => Promise<string> = null, confirmationMessage: string = null): Promise<boolean> {
        if (!await checkErrors()) return false;
        const warnings = getWarnings ? await getWarnings() : null;
        if (!warnings) {
            return true;
        }
        return await AppUtils.showConfirmationDialog(warnings + Base.lf + (confirmationMessage || Translations.DoYouReallyWantToSave)) === ConfirmationDialogResult.Yes;
    }

    static async cancel(newStateHash: string, orgStateHash: string, confirmationMessage: string = null): Promise<ConfirmationDialogResult> {
        if (newStateHash !== orgStateHash) {
            return await AppUtils.showConfirmationDialog(confirmationMessage ?? Translations.YouHaveNotSavedChangesDoYouWantToSaveChanges, true);
        } else {
            return Base.getPromiseResult<ConfirmationDialogResult>(ConfirmationDialogResult.No);
        }
    }

    static async cancelEditDialog(checkChanges: boolean, newStateHash: string, orgStateHash: string,
        handleSave: () => void, handleCancel: () => void, confirmationMessage: string = null): Promise<void> {
        if (checkChanges) {
            const cancelResult = await AppUtils.cancel(newStateHash, orgStateHash, confirmationMessage);
            if (cancelResult === ConfirmationDialogResult.Cancel) return;
            if (cancelResult === ConfirmationDialogResult.Yes) {
                handleSave();
            } else if (handleCancel) {
                handleCancel();
            }
        } else if (handleCancel) {
            handleCancel();
        }
    }

    static async canMoveOut(newStateHash: string, orgStateHash: string,
        saveToDb: () => Promise<boolean>,
        refreshList: () => Promise<void>,
        resetStateHash: (newStateHash: string) => void = null): Promise<boolean> {
        const cancelResult = await AppUtils.cancel(newStateHash, orgStateHash);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await saveToDb()) return false;
            if (refreshList) {
                await refreshList();
            }
        }
        if (cancelResult === ConfirmationDialogResult.No && newStateHash !== orgStateHash) {
            if (resetStateHash) {
                resetStateHash(newStateHash);
            }
        }
        return true;
    };

    static validateFileUploadSize(fileSizeKb: number, errorMessage: string = null, maxUploadSize: number = null): boolean {
        const maxFileUploadSize = maxUploadSize ?? appConfig.maxFileUploadSize;
        if (fileSizeKb > maxFileUploadSize) {
            AppUtils.showErrorMessage(String.format(errorMessage ?? Translations.MaximumSizeOfFilesThatCanBeAddedInBatchIsParameterMB, (maxFileUploadSize / 1024).toLocaleFixed(0)));
            return false;
        }
        return true;
    }

    static validateFileAttachment(fileAttachment: FileAttachment, errorMessage: string = null, maxUploadSize: number = null): boolean {
        if (!fileAttachment) return false;
        return AppUtils.validateFileUploadSize(fileAttachment.size / 1024, errorMessage, maxUploadSize);
    }
    
    static validateFileAttachments(fileAttachments: FileAttachment[], errorMessage: string = null, maxUploadSize: number = null): boolean {
        if (!fileAttachments || fileAttachments.length < 1) return true;
        let fileSizeKb = 0;
        for (const fileAttachment of fileAttachments) {
            if (!fileAttachment) continue;
            fileSizeKb = fileSizeKb + fileAttachment.size / 1024;
        }
        return AppUtils.validateFileUploadSize(fileSizeKb, errorMessage, maxUploadSize);
    }

    //App session
    static setNewInstantMessages(newInstantMessages: IInstantMessageListItem[]) {
        store.customStore.dispatch(storeActions.setNewInstantMessages(newInstantMessages));
    }

    static runLongTask<T>(task: () => T): T {
        if (!task) return null;
        store.customStore.dispatch(storeActions.fetchStart());
        try {
            return task();
        } finally {
            store.customStore.dispatch(storeActions.fetchEnd());
        }
    };
}